import React, { FC, memo, useMemo, useContext, useEffect, useCallback, useState } from 'react';
import { Field, FormikProps, useFormikContext } from 'formik';
import getTime from 'date-fns/getTime';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';

import InfoIcon from '@mui/icons-material/Info';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';

import Spinner from '../../../../components/Spinner';
import RenderInput from '../../../../components/RenderInput';
import AutocompleteByOptionId from '../../../../components/AutocompleteByOptionId';
import { Context as CurrenciesContext } from '../../../../api/currencies/context';
import { Context as ExchangeRatesContext } from '../../../../api/exchangeRates/context';
import { Context as CountriesContext } from '../../../../api/countries/context';
import { Context as BankAccountsContext } from '../../../../api/bankAccounts/context';
import { Context as GeneralAccountsContext } from '../../../../api/generalAccounts/context';
import { Context as ClientsContext } from '../../../../api/clients/context';
import { Client } from '../../../../api/clients/types';
import BankTransferSubForm from '../subForms/BankTransferSubForm';
import {
    PaymentMethodTypes,
    paymentMethodTypeEnum,
    paymentMethodTypesOptionsWithoutVoucher
} from '../../../../api/vouchers/helpers';
import FormFieldArray from '../../../../components/FormFieldArray';
import RowCheckFields, {
    getCheckLocalAmountLabel,
    getCheckSummary,
    getEmptyCheck
} from '../../../../subForms/RowCheckFields/RowCheckFields';
import { taytanFormatNumberToString } from '../../../../utils/mathUtils';
import { sameCurrency } from '../../../../utils/currencyMath';
import { VoucherField } from '../../../../subForms/RowVoucherFields/RowVoucherFields';
import { Currency } from '../../../../api/currencies/types';
import { displayToast } from '../../../../components/RTToast';
import { isDateValid } from '../../../../utils/dateUtils';
import { getDataNameAnd_id } from '../../../../utils/arrayUtils';
import { Voucher } from '../../../../api/vouchers/types';
import PaymentsTerminalField from '../../../../components/PaymentsTerminalField';
import AmountCurrencyRateBox from '../subForms/AmountCurrencyRateBox';
import { LoaderOnComponent } from '../../../../components/Loader/Loader';

import CollapsibleSection from '../../../../components/CollapsibleSection/CollapsibleSection';
import AgentBeneficiaryFormField from '../../../Reservations/TravelFile/TravelFileHomePage/AccountingSection/GeneratePdfs/AgentBeneficiaryFormField';
import receiptFormStyles from './receiptFormStyles';
import CreditCardSubForm from '../subForms/CreditCardSubForm';
import SavedCardPicker from '../../../../components/SavedCardPicker';
import CreditCardPaymentPlanFields from '../subForms/CreditCardPaymentPlanFields';
import usePaymentPlanInfo from '../../../../hooks/usePaymentPlanInfo';

type FormProps = {
    beneficiaryType: string;
    documentGenerationData?: Partial<Voucher> | null;
    setPaymentMethodType: React.Dispatch<React.SetStateAction<PaymentMethodTypes>>;
    setIsAci: React.Dispatch<React.SetStateAction<boolean>>;
    open: boolean;
    toggleOpen: any;
    setIsPaidWithCreditCard: (flag: boolean) => void;
    /** Tells the dialog to label its action "Charge Saved Card". */
    setIsSavedCardSelected?: (flag: boolean) => void;
    isNextToMasleka: boolean;
    setIsNextToMasleka: (flag: boolean) => void;
    // Inline card flow: issue the Receipt in place once the charge is approved,
    // so the PDF opens without reloading the page.
    onCardPaymentSuccess?: (values: any) => void;
};

// Form component:
const ReceiptForm: FC<FormProps> = (props: FormProps) => {
    const {
        beneficiaryType,
        documentGenerationData,
        setPaymentMethodType,
        setIsAci,
        open,
        toggleOpen,
        setIsPaidWithCreditCard,
        setIsSavedCardSelected,
        isNextToMasleka,
        setIsNextToMasleka,
        onCardPaymentSuccess
    } = props;
    const formProps = useFormikContext() as FormikProps<any>;
    const { setFieldValue, isSubmitting, values, initialValues } = formProps;
    const classes = receiptFormStyles();

    const [loadingText, setLoadingText] = useState<string>('');
    const justResetRef = React.useRef(false);

    const roundTo2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;
    const [amountLeftToPay, setAmountLeftToPay] = useState<number>(
        roundTo2(documentGenerationData?.info.leftToPay || 0)
    );
    const [amountDiffValue, setAmountDiffValue] = useState<number>(0);
    const [userChangedAmount, setUserChangedAmount] = useState<boolean>(false);
    const currencies = useContext(CurrenciesContext);
    const { getConvertRate, getRateForDate } = useContext(ExchangeRatesContext);
    const exchangeRatesContext = useContext(ExchangeRatesContext);
    const countriesContext = useContext(CountriesContext);
    const bankAccountsContext = useContext(BankAccountsContext);
    const generalAccountsOptions = useContext(GeneralAccountsContext);
    const clientsContext = useContext(ClientsContext);
    const localCurrency: string = (clientsContext.selected as Client)?.currencySettings
        ?.localCurrency as string;

    const originalAmount: number = roundTo2(documentGenerationData?.info.leftToPay || 0);

    useEffect(() => {
        if (open) {
            justResetRef.current = true;
            formProps.resetForm();
            setAmountLeftToPay(roundTo2(documentGenerationData?.info.leftToPay || 0));
            setUserChangedAmount(false);
            // Set the correct exchange rate from the exchange rates table
            const currencyId =
                typeof initialValues.currency === 'object'
                    ? (initialValues.currency as any)?._id
                    : initialValues.currency;
            if (currencyId && localCurrency && currencyId !== localCurrency) {
                const rate = getConvertRate(currencyId, localCurrency);
                const exchangeRate = (rate as { exchangeRateSell: number })?.exchangeRateSell;
                if (exchangeRate) {
                    setFieldValue('rate', exchangeRate);
                }
            }
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setFieldValue('amount', +amountLeftToPay);
        setAmountDiffValue(roundTo2(originalAmount - amountLeftToPay));
    }, [amountLeftToPay, originalAmount]);

    // Sync amountLeftToPay when values.amount is updated externally (e.g., RateControlBox currency conversion)
    useEffect(() => {
        const formAmount = Number(values.amount) || 0;
        if (formAmount !== Number(amountLeftToPay)) {
            setAmountLeftToPay(formAmount);
        }
    }, [values.amount]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setFieldValue('amountDiff', amountDiffValue);
    }, [amountDiffValue]);

    const currencyOptions = useMemo(() => currencies.dataAsArray, [currencies.dataAsArray]);
    const currencySymbol = useMemo(
        () => currencies.getSymbolByCurrency(values.currency),
        [values.currency, currencies]
    );
    // Pelecard (the Masleka gateway) only ever settles in ILS or USD — restrict the
    // currency picker to just those two while Credit Card is the payment method,
    // matching tourRegistrationApp's rules (docs/tour-registration-payment-rules.md).
    const ilsUsdCurrencyOptions = useMemo(
        () => currencyOptions.filter((c: Currency) => c.code === 'ILS' || c.code === 'USD'),
        [currencyOptions]
    );
    const { tripDate, fullPaymentWindowDays } = usePaymentPlanInfo(documentGenerationData?.travelFileId);
    const countryOptions = useMemo(() => getDataNameAnd_id(countriesContext.data), [countriesContext.data]);

    const updateFieldArrayAmountsByNewRate = useCallback(
        (fieldArrayName: 'checks', newRate: number) => {
            values.paymentMethodObject?.[fieldArrayName].forEach((field: VoucherField, idx: number) => {
                const newAmount = (field.amount / (values?.rate || 1)) * (newRate || 1);
                setFieldValue(`paymentMethodObject.${fieldArrayName}[${idx}].amount`, newAmount);
            });
        },
        [setFieldValue, values.paymentMethodObject, values.rate]
    );

    const getNewRateByDateToChange = useCallback(
        async (date: Date | null) => {
            const currId = typeof values.currency === 'object' ? values.currency?._id : values.currency;
            const dateTimeStamp = getTime(new Date(date || new Date()));
            // Try in-memory cache first (instant, no API call)
            const cached = getRateForDate(currId, localCurrency, dateTimeStamp);
            if (cached) {
                setFieldValue('rate', cached.exchangeRateSell);
                return cached.exchangeRateSell;
            }
            // Fallback to API
            setLoadingText('Calc Rate...');
            return exchangeRatesContext
                .get({ date: dateTimeStamp })
                .then((res) => {
                    const rate = getConvertRate(currId, localCurrency, res);
                    const rateByDate: number = (rate as { exchangeRateSell: number })?.exchangeRateSell || 1;
                    if (rateByDate) {
                        setFieldValue('rate', rateByDate);
                        setLoadingText('');
                        return rateByDate;
                    } else {
                        const rate2 = getConvertRate(currId, localCurrency);
                        const todayRate: number =
                            (rate2 as { exchangeRateSell: number })?.exchangeRateSell || 1;
                        setFieldValue('rate', todayRate);
                        setLoadingText('');
                        return todayRate;
                    }
                })
                .catch(() => {
                    displayToast('error', 'Error to get updated rate!', '');
                    setLoadingText('');
                    return undefined;
                });
        },
        [exchangeRatesContext, getConvertRate, getRateForDate, values.currency, localCurrency, setFieldValue]
    );

    // Compute the effective value date from the active payment method to sync with RateControlBox.
    // For BANK_TRANSFER and CHECK, only override when the user has enabled the toggle.
    const effectiveValueDate = useMemo(() => {
        if (values.paymentMethodType === paymentMethodTypeEnum.BANK_TRANSFER) {
            if (!values.paymentMethodObject.overrideRateByValueDate) return null;
            return values.paymentMethodObject.valueDate || null;
        }
        if (values.paymentMethodType === paymentMethodTypeEnum.CHECK) {
            if (!values.paymentMethodObject.overrideRateByValueDate) return null;
            return values.paymentMethodObject?.checks?.[0]?.valueDate || null;
        }
        return null;
    }, [
        values.paymentMethodType,
        values.paymentMethodObject.valueDate,
        values.paymentMethodObject.overrideRateByValueDate,
        values.paymentMethodObject?.checks?.[0]?.valueDate
    ]); // eslint-disable-line

    const [firstFieldArrayValueDate, setFirstFieldArrayValueDate] = useState<Date | null>(null);

    useEffect(() => {
        let fieldArrayName: 'checks' | undefined = (() => {
            if (values.paymentMethodType === paymentMethodTypeEnum.CHECK) return 'checks';
        })();
        if (!fieldArrayName) return;
        // Respect the "Override rate by value date" toggle for CHECK
        if (!values.paymentMethodObject.overrideRateByValueDate) return;
        // A local-currency receipt has no document→local rate to look up:
        // getConvertRate(₪, ₪) finds no row and falls back to 1, which would
        // both clobber the local↔PRIMARY rate the box shows and rescale every
        // check amount by 1/rate. RateControlBox already tracks the date for
        // this case; leave the rate to it.
        const documentCurrencyId =
            typeof values.currency === 'object' ? values.currency?._id : values.currency;
        if (sameCurrency(documentCurrencyId, localCurrency)) return;
        const date = values.paymentMethodObject?.[fieldArrayName]?.[0]?.valueDate;
        if (values.paymentMethodObject?.[fieldArrayName]?.[0] && date !== firstFieldArrayValueDate) {
            if (date === null || isDateValid(date)) {
                getNewRateByDateToChange(date).then((newRate?: number) => {
                    if (newRate) {
                        updateFieldArrayAmountsByNewRate('checks', newRate);
                    }
                });
                setFirstFieldArrayValueDate(date);
            }
        }
    }, [
        firstFieldArrayValueDate,
        getNewRateByDateToChange,
        updateFieldArrayAmountsByNewRate,
        values.paymentMethodObject,
        values.paymentMethodObject.checks,
        values.paymentMethodType,
        values.currency,
        localCurrency
    ]);

    const renderAmountField = useMemo<boolean>(
        () => (values.paymentMethodType === paymentMethodTypeEnum.CHECK ? false : true),
        [values.paymentMethodType]
    );

    // ── Cheque receipts: the amount IS the sum of the cheques ────────────────
    // The Amount field is hidden for CHECK (above) because the figure belongs to
    // the cheque rows — but the derivation was never implemented, so `amount`
    // stayed at the invoice's leftToPay (or 0) while the rows carried the real
    // numbers. On the PDF the rows fall back to each cheque's own amount while
    // the TOTAL reads `info.amount` with no fallback, which is why a cheque
    // receipt printed per-cheque amounts above a TOTAL of 0, or a TOTAL that
    // disagreed with the rows in the same currency.
    const checksTotal = useMemo<number>(() => {
        if (values.paymentMethodType !== paymentMethodTypeEnum.CHECK) return 0;
        const checks = values.paymentMethodObject?.checks;
        if (!Array.isArray(checks)) return 0;
        return roundTo2(checks.reduce((sum: number, check: any) => sum + (Number(check?.amount) || 0), 0));
    }, [values.paymentMethodType, values.paymentMethodObject?.checks]);

    // Feed it through amountLeftToPay rather than setting `amount` directly, so the
    // existing amountLeftToPay → amount → amountDiff pipeline stays consistent.
    // Deliberately does NOT set userChangedAmount: that flag opens the C/D-note
    // block, and a derived total is not the user electing to part-pay an invoice.
    useEffect(() => {
        if (values.paymentMethodType !== paymentMethodTypeEnum.CHECK) return;
        if (checksTotal !== Number(amountLeftToPay)) setAmountLeftToPay(checksTotal);
    }, [checksTotal, values.paymentMethodType]); // eslint-disable-line react-hooks/exhaustive-deps

    // Warn, don't block: the cheques may legitimately not settle the invoice in
    // full, but a silent mismatch is how the wrong figure reached the PDF.
    // Only meaningful when this receipt actually covers an invoice. A standalone
    // receipt (Issue Documents, nothing selected to cover) has no balance, so
    // originalAmount falls back to 0 and every cheque total "mismatched" it —
    // warning about an invoice that isn't there.
    const checksMismatch =
        values.paymentMethodType === paymentMethodTypeEnum.CHECK &&
        Array.isArray(values.paymentMethodObject?.checks) &&
        values.paymentMethodObject.checks.length > 0 &&
        originalAmount > 0 &&
        checksTotal !== originalAmount;

    // ACI is auto-issued only when:
    //   - beneficiary is an agent
    //   - the invoice has a real commission (> 0) — Galor invoices sometimes carry
    //     vatOnCommission with zero commission; don't emit a phantom ACI for those
    //   - this receipt fully covers the invoice (amountDiff === 0)
    const invoiceCommission: number = Number(documentGenerationData?.info?.commission) || 0;
    const hasCommission = invoiceCommission > 0;
    const isFullyCovered = amountDiffValue === 0;
    const aciEnabled = hasCommission && isFullyCovered;
    const aciField = useMemo(() => {
        // No agent, or invoice has no commission → no ACI involvement at all.
        if (beneficiaryType !== 'agent' || !hasCommission) {
            setIsAci(false);
            return <></>;
        }
        setIsAci(aciEnabled);
        return (
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <Field
                        component={RenderInput}
                        label="ACI Reference No."
                        name="aciRef"
                        disabled={isSubmitting || !aciEnabled}
                        fullWidth
                        required={aciEnabled}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Box
                        sx={{
                            bgcolor: '#e8f4fd',
                            border: '1px solid #90caf9',
                            borderRadius: 1,
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1
                        }}
                    >
                        <InfoIcon fontSize="small" color="primary" sx={{ mt: 0.25 }} />
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                            {aciEnabled
                                ? `This transaction includes a commission of ${taytanFormatNumberToString(
                                      invoiceCommission
                                  )} + ${taytanFormatNumberToString(
                                      documentGenerationData?.info?.vatOnCommission || 0
                                  )} VAT. The commission will be deducted from the payment and an ACI will be generated automatically.`
                                : `ACI will be issued automatically once the invoice is fully covered (commission ${taytanFormatNumberToString(
                                      invoiceCommission
                                  )} + ${taytanFormatNumberToString(
                                      documentGenerationData?.info?.vatOnCommission || 0
                                  )} VAT).`}
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
        );
    }, [
        beneficiaryType,
        hasCommission,
        aciEnabled,
        invoiceCommission,
        setIsAci,
        documentGenerationData,
        isSubmitting
    ]);

    useEffect(() => {
        setIsPaidWithCreditCard(values.paymentMethodType === paymentMethodTypeEnum.CREDIT_CARD);
    }, [values.paymentMethodType]);

    useEffect(() => {
        setIsSavedCardSelected?.(
            values.paymentMethodType === paymentMethodTypeEnum.CREDIT_CARD && !!values.savedCardPayment?.cardId
        );
    }, [values.paymentMethodType, values.savedCardPayment?.cardId, setIsSavedCardSelected]);

    const onClose = () => {
        setIsNextToMasleka(false);
        toggleOpen();
    };

    return (
        <>
            <Box p={3} className={classes.positionRelative}>
                {loadingText && <LoaderOnComponent text={loadingText} />}
                <Grid container spacing={4} justifyContent="space-between">
                    <Grid container item direction="column" spacing={2} xs={12} sm={6}>
                        <CollapsibleSection label="Receipt Details" card>
                            <Stack spacing={2}>
                                <Field
                                    component={RenderInput}
                                    label="For"
                                    name="for"
                                    disabled={isSubmitting}
                                    fullWidth
                                    margin="none"
                                />
                                {beneficiaryType === 'agent' ? (
                                    <AgentBeneficiaryFormField
                                        countryOptions={countryOptions}
                                        hideBeneficiaryField
                                    />
                                ) : (
                                    <Field
                                        component={RenderInput}
                                        label="Beneficiary"
                                        name="beneficiary"
                                        disabled
                                        fullWidth
                                        margin="none"
                                    />
                                )}
                                <AmountCurrencyRateBox
                                    documentGenerationData={documentGenerationData}
                                    amountValue={amountLeftToPay}
                                    onAmountChange={(raw) => {
                                        setAmountLeftToPay(raw);
                                        setUserChangedAmount(true);
                                    }}
                                    disabled={isSubmitting}
                                    hideAmount={!renderAmountField}
                                    rateDateOverride={effectiveValueDate}
                                    disableRateControl={
                                        (values.paymentMethodType === paymentMethodTypeEnum.BANK_TRANSFER ||
                                            values.paymentMethodType === paymentMethodTypeEnum.CHECK) &&
                                        !!values.paymentMethodObject.overrideRateByValueDate
                                    }
                                    currencyOptionsOverride={
                                        values.paymentMethodType === paymentMethodTypeEnum.CREDIT_CARD
                                            ? ilsUsdCurrencyOptions
                                            : undefined
                                    }
                                />
                                <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                        <Field
                                            component={RenderInput}
                                            type="checkbox"
                                            label="Print rate of exchange"
                                            name="printExchangeRate"
                                            disabled={isSubmitting}
                                        />
                                    </Grid>
                                </Grid>
                                {aciField}
                                <Field
                                    component={RenderInput}
                                    label="Remark"
                                    name="remark"
                                    multiline
                                    rows={2}
                                    disabled={isSubmitting}
                                    fullWidth
                                    margin="none"
                                />
                            </Stack>
                        </CollapsibleSection>
                    </Grid>
                    <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                    <Grid container item direction="column" xs={12} sm>
                        <CollapsibleSection label="Payment Method" card>
                            <FormControl variant="standard" disabled={isSubmitting}>
                                <RadioGroup
                                    row
                                    defaultValue={paymentMethodTypeEnum.CREDIT_CARD}
                                    name="paymentMethodType"
                                    onChange={(ev) => {
                                        setPaymentMethodType(ev.target.value as PaymentMethodTypes);
                                        setFieldValue('paymentMethodType', ev.target.value);
                                    }}
                                >
                                    {paymentMethodTypesOptionsWithoutVoucher.map((paymentMethodType) => {
                                        return (
                                            <FormControlLabel
                                                key={paymentMethodType}
                                                control={<Radio />}
                                                value={paymentMethodType}
                                                label={paymentMethodType}
                                            />
                                        );
                                    })}
                                </RadioGroup>
                            </FormControl>
                            <Grid item xs={12}>
                                {(() => {
                                    switch (values.paymentMethodType) {
                                        case paymentMethodTypeEnum.BANK_TRANSFER: {
                                            return (
                                                <BankTransferSubForm
                                                    formProps={formProps}
                                                    showRateOverrideCheckbox
                                                />
                                            );
                                        }
                                        case paymentMethodTypeEnum.CREDIT_CARD: {
                                            return (
                                                <>
                                                    <PaymentsTerminalField disabled={isSubmitting} />
                                                    {/* Saved cards belong to passengers: offered when the
                                                        payer is the travel file's passenger, shown disabled
                                                        (with the reason) for an agent or supplier payer. */}
                                                    <SavedCardPicker
                                                        scope="travelFile"
                                                        currencyCode={
                                                            values.currency?.code ||
                                                            currencies.getCodeByCurrency(values.currency)
                                                        }
                                                        travelFileId={
                                                            beneficiaryType === 'passenger'
                                                                ? documentGenerationData?.travelFileId
                                                                : null
                                                        }
                                                        disabled={isSubmitting}
                                                        unavailableReason="Saved cards are offered only when the payer is a passenger on a travel file"
                                                    />
                                                    {!values.savedCardPayment?.cardId && (
                                                        <CreditCardPaymentPlanFields
                                                            tripDate={tripDate}
                                                            fullPaymentWindowDays={fullPaymentWindowDays}
                                                            disabled={isSubmitting}
                                                            allowDollarLinked={!!documentGenerationData?.travelFileId}
                                                        />
                                                    )}
                                                    {isNextToMasleka && !values.savedCardPayment?.cardId && (
                                                        <CreditCardSubForm
                                                            open={open}
                                                            toggleOpen={() => setIsNextToMasleka(false)}
                                                            isLoading={false}
                                                            values={values}
                                                            documentGenerationData={documentGenerationData}
                                                            tripDate={tripDate}
                                                            resumeReceipt
                                                            onPaymentSuccess={onCardPaymentSuccess}
                                                        />
                                                    )}
                                                </>
                                            );
                                        }
                                        case paymentMethodTypeEnum.CASH: {
                                            return (
                                                <Field
                                                    component={RenderInput}
                                                    label="Details"
                                                    name="paymentMethodObject.details"
                                                    multiline
                                                    rows={3}
                                                    fullWidth
                                                    disabled={isSubmitting}
                                                />
                                            );
                                        }
                                        case paymentMethodTypeEnum.CHECK: {
                                            return (
                                                <>
                                                    <FormFieldArray
                                                        fieldName="paymentMethodObject.checks"
                                                        getEmptyRow={getEmptyCheck}
                                                        rowFields={RowCheckFields}
                                                        label="Check"
                                                        collapsible
                                                        getRowSummary={getCheckSummary(
                                                            values.currency?.symbol
                                                        )}
                                                        propsToRowFields={{
                                                            currencySymbol: values.currency?.symbol,
                                                            // The check is entered in the document
                                                            // currency; amount * rate = local, per
                                                            // RateControlBox's own conversion, so
                                                            // this equivalent is a LOCAL figure and
                                                            // takes the local symbol. Suppressed
                                                            // entirely for a local-currency
                                                            // document — see the helper.
                                                            getExtraRowAmountLabel: getCheckLocalAmountLabel(
                                                                values.currency,
                                                                localCurrency,
                                                                values?.rate,
                                                                currencies.getSymbolByCurrency(localCurrency) || ''
                                                            )
                                                        }}
                                                    />
                                                    <Field
                                                        type="checkbox"
                                                        component={RenderInput}
                                                        name="paymentMethodObject.overrideRateByValueDate"
                                                        label="Override rate by first check's value date"
                                                        disabled={isSubmitting}
                                                    />
                                                    {/* The receipt total is the sum of the cheques.
                                                        Show it, and flag a mismatch against the
                                                        invoice — informational, never blocking. */}
                                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                                        {`Receipt total (sum of checks): ${taytanFormatNumberToString(
                                                            checksTotal
                                                        )}${values.currency?.symbol || ''}`}
                                                    </Typography>
                                                    {checksMismatch && (
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ mt: 0.5, color: 'warning.main' }}
                                                        >
                                                            {`Does not match the invoice balance of ${taytanFormatNumberToString(
                                                                originalAmount
                                                            )}${values.currency?.symbol || ''}.`}
                                                        </Typography>
                                                    )}
                                                </>
                                            );
                                        }
                                    }
                                })()}
                            </Grid>
                            {/* {originalAmount !== +amountValue && ( */}
                            {userChangedAmount && originalAmount !== +amountLeftToPay && (
                                <Grid item xs={12} sm={5}>
                                    <Field
                                        style={{ marginTop: '10px' }}
                                        component={RenderInput}
                                        type="checkbox"
                                        name="issueCD"
                                        label="C/D"
                                        styleFormControlLabel={classes.marginAutoAuto0}
                                        disabled={isSubmitting}
                                    />
                                    <RenderInput
                                        type="number"
                                        field={{
                                            name: 'amountDiff',
                                            value: amountDiffValue
                                        }}
                                        label="Amount"
                                        disabled={true}
                                    />
                                    <Field
                                        component={AutocompleteByOptionId}
                                        name="accountName"
                                        label="General Account"
                                        options={generalAccountsOptions.data}
                                        nameKey="accountName"
                                        fullOption
                                        onChange={setFieldValue}
                                        disabled={isSubmitting}
                                        required
                                    />
                                </Grid>
                            )}
                        </CollapsibleSection>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default memo(ReceiptForm);
